// WordprocessingML (`DocxWriter`, SuiteCompile/DocxWriter.swift): Shunn manuscripts, submission documents with their
// letterhead and title block, posts, reader copies. Custom document properties record the project, checkpoint and
// reader a copy came from, so a reviewer's copy can be matched when it comes back (`ReviewImporter`).
import {
  MAX_VERSE_INDENT, sha256Hex, startsList, verseLines, type Alignment, type Block, type InkColor, type Inline, type InlineStyle, type ListStyle,
} from '@writing-suite/core';
import {
  authorNames, BOOK_PAGE_KINDS, contactName, contentsEntries, documentTitle, hasTitlePageMatter, header as fillHeader, heading, isDocument,
  pageInches, swiftRound, titlePageContent,
} from './manuscript.ts';
import type { BookMatter, CompiledManuscript, CompiledSection, RenderSettings, TitlePageContent } from './types.ts';
import { iso8601 } from './epub.ts';
import { keepsMarks } from './pipeline.ts';
import { xmlEscape as escape } from './xml.ts';
import { ZipWriter } from './zip.ts';

export const PROJECT_PROPERTY = 'WritingSuiteProject';
export const CHECKPOINT_PROPERTY = 'WritingSuiteCheckpoint';
/** The beta reader a copy was made for. */
export const READER_PROPERTY = 'WritingSuiteReader';

const PLAIN: InlineStyle = { italic: false, bold: false, smallCaps: false, underline: false, highlight: null, color: null };
const BOLD: InlineStyle = { ...PLAIN, bold: true };

/** `whole`: clamped and rounded, so a stray value never produces an unreadable number. */
export function whole(value: number, lo: number, hi: number): number {
  if (Number.isNaN(value)) return lo;
  return swiftRound(Math.min(Math.max(value, lo), hi));
}
export const twips = (inches: number) => whole(inches * 1440, -100_000, 100_000);

export interface DocxOptions {
  /** The core properties' created/modified date; defaults to now. */
  date?: Date;
  /** Word custom document properties (`WritingSuiteProject`, `WritingSuiteCheckpoint`, `WritingSuiteReader`). */
  properties?: Record<string, string>;
  /** `{year}` in a document's title lines; the Swift takes today's. */
  year?: number;
}

/** Word numbering for the manuscript's lists: an abstract definition per style, a `w:num` per list. */
class Lists {
  lists: { style: ListStyle; start: number }[] = [];
  static styles: ListStyle[] = ['bullet', 'circle', 'dash', 'decimal', 'decimalParen', 'lowerAlpha', 'upperAlpha', 'lowerRoman', 'upperRoman'];

  /** The `w:numId` of a list item (1-based), starting a new list at its first item. */
  number(block: Block, previous: Block | null): number | null {
    if (block.kind.type !== 'listItem') return null;
    if (startsList(block, previous) || !this.lists.length) this.lists.push({ style: block.kind.style, start: block.kind.number });
    return this.lists.length;
  }

  static format(style: ListStyle): [string, string] {
    switch (style) {
      case 'bullet': return ['bullet', '•'];
      case 'circle': return ['bullet', '◦'];
      case 'dash': return ['bullet', '–'];
      case 'decimal': return ['decimal', '%1.'];
      case 'decimalParen': return ['decimal', '%1)'];
      case 'lowerAlpha': return ['lowerLetter', '%1.'];
      case 'upperAlpha': return ['upperLetter', '%1.'];
      case 'lowerRoman': return ['lowerRoman', '%1.'];
      case 'upperRoman': return ['upperRoman', '%1.'];
    }
  }

  get numberingXML(): string {
    const styles = Lists.styles;
    const used = styles.filter((s) => this.lists.some((l) => l.style === s));
    const abstract = used.map((style) => {
      const [numFmt, text] = Lists.format(style);
      return `<w:abstractNum w:abstractNumId="${styles.indexOf(style)}"><w:multiLevelType w:val="singleLevel"/>`
        + `<w:lvl w:ilvl="0"><w:start w:val="1"/><w:numFmt w:val="${numFmt}"/><w:lvlText w:val="${text}"/>`
        + '<w:lvlJc w:val="left"/><w:pPr><w:ind w:left="720" w:hanging="360"/></w:pPr></w:lvl></w:abstractNum>';
    }).join('');
    const nums = this.lists.map((list, i) => `<w:num w:numId="${i + 1}"><w:abstractNumId w:val="${styles.indexOf(list.style)}"/>`
      + `<w:lvlOverride w:ilvl="0"><w:startOverride w:val="${Math.max(1, list.start)}"/></w:lvlOverride></w:num>`).join('');
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:numbering xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">${abstract}${nums}</w:numbering>`;
  }
}

/** The parts of the .docx in order (the zip is `writeDocx`). */
export function docxEntries(m: CompiledManuscript, o: DocxOptions = {}): [string, string][] {
  const r = m.preset.render;
  const properties = o.properties ?? {};
  const hasProperties = Object.keys(properties).length > 0;
  let body = '';

  const content = titlePageContent(m);
  if (content) body += titlePage(content, m);
  if (isDocument(m)) body += documentTop(m, o.year);

  let first = true;
  const lists = new Lists();
  for (const section of m.sections) {
    // Book matter with its own layout: centred pages, the copyright notice, a contents list.
    if (section.matter !== null && BOOK_PAGE_KINDS.has(section.matter)) {
      body += bookMatter(section.matter, section, m, !first || (r.titlePage.include && !hasTitlePageMatter(m)));
      first = false;
      continue;
    }
    switch (section.role) {
      case 'part':
        body += headingParagraphs(heading(r.partHeading, section), 'PartHeading', r.chapterStartsNewPage || !first);
        break;
      case 'chapter':
        body += headingParagraphs(heading(r.chapterHeading, section), 'ChapterHeading', r.chapterStartsNewPage && !(first && !r.titlePage.include));
        break;
      case 'front-matter': case 'back-matter':
        body += headingParagraphs([section.title], 'ChapterHeading', true);
        break;
      default:
        if (section.followsScene) body += paragraph('SceneBreak', run(r.sceneBreak, PLAIN, m));
    }
    let afterHeading = section.role !== 'scene';
    let previous: Block | null = null;
    for (const block of section.blocks) {
      body += blockXML(block, m, afterHeading, lists.number(block, previous));
      afterHeading = block.kind.type === 'sceneBreak';
      previous = block;
    }
    first = false;
  }
  if (r.endMarker !== '') body += paragraph('SceneBreak', run(r.endMarker, PLAIN, m));

  const { width, height } = pageInches(r.page.size);
  const margin = twips(r.page.margins);
  const hasHeader = r.header !== '' || m.readerMark !== null;
  const sectPr = `<w:sectPr>${hasHeader ? '<w:headerReference w:type="default" r:id="rIdHeader1"/><w:headerReference w:type="first" r:id="rIdHeader2"/>' : ''}`
    + `<w:pgSz w:w="${twips(width)}" w:h="${twips(height)}"/>`
    + `<w:pgMar w:top="${margin}" w:right="${margin}" w:bottom="${margin}" w:left="${margin}" w:header="${twips(0.5)}" w:footer="${twips(0.5)}" w:gutter="0"/>`
    + `${r.titlePage.include ? '<w:titlePg/>' : ''}</w:sectPr>`;
  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><w:body>${body}${sectPr}</w:body></w:document>`;

  const numbering = lists.lists.length > 0;
  const parts: [string, string][] = [];
  parts.push(['[Content_Types].xml', contentTypes(hasHeader, hasProperties, numbering)]);
  parts.push(['_rels/.rels', rootRels(hasProperties)]);
  parts.push(['word/document.xml', document]);
  parts.push(['word/styles.xml', styles(r, m.language)]);
  parts.push(['word/settings.xml', SETTINGS]);
  parts.push(['word/_rels/document.xml.rels', documentRels(hasHeader, numbering, linkAddresses(m))]);
  if (numbering) parts.push(['word/numbering.xml', lists.numberingXML]);
  if (hasHeader) {
    parts.push(['word/header1.xml', headerXML(fillHeader(r.header, m), m.readerMark)]);
    // The title page has no running head, but a reader's copy still names them.
    parts.push(['word/header2.xml', m.readerMark !== null ? headerXML('', m.readerMark) : EMPTY_HEADER]);
  }
  parts.push(['docProps/core.xml', core(m, o.date ?? new Date())]);
  parts.push(['docProps/app.xml', app(m)]);
  if (hasProperties) parts.push(['docProps/custom.xml', custom(properties)]);
  return parts;
}

/** `DocxWriter.write`. */
export function writeDocx(m: CompiledManuscript, o: DocxOptions = {}): Uint8Array {
  const zip = new ZipWriter();
  for (const [name, text] of docxEntries(m, o)) zip.add(name, text);
  return zip.finish();
}

// MARK: Body

function titlePage(content: TitlePageContent, m: CompiledManuscript): string {
  let xml = '';
  if (content.contact.length) {
    // Contact block top left; word count on the first line, right-aligned by tab.
    content.contact.forEach((line, i) => {
      const tab = i === 0 && content.wordCount !== null ? '<w:r><w:tab/></w:r>' + run(content.wordCount, PLAIN, m) : '';
      xml += paragraph('Contact', run(line, PLAIN, m) + tab);
    });
  } else if (content.wordCount !== null) {
    xml += paragraph('ContactRight', run(content.wordCount, PLAIN, m));
  }
  xml += paragraph('TitlePageTitle', run(content.title, PLAIN, m));
  if (content.byline !== null) xml += paragraph('Byline', run(content.byline, PLAIN, m));
  return xml;
}

/** A letter's contact block and a document's centred title lines, above a synopsis or query letter. */
function documentTop(m: CompiledManuscript, year?: number): string {
  const r = m.preset.render;
  let xml = '';
  if (r.letterhead === true && m.author) {
    xml += [contactName(m.author), ...m.author.contact].map((l) => paragraph('Contact', run(l, PLAIN, m))).join('');
    xml += paragraph('Contact', '');
  }
  const lines = documentTitle(m, year);
  lines.forEach((line, i) => { xml += paragraph('Byline', run(line, i === 0 ? BOLD : PLAIN, m)); });
  if (lines.length) xml += paragraph('Byline', '');
  return xml;
}

function bookMatter(matter: BookMatter, section: CompiledSection, m: CompiledManuscript, newPage: boolean): string {
  const pageBreak = newPage ? '<w:p><w:r><w:br w:type="page"/></w:r></w:p>' : '';
  const blocks = section.blocks.filter((b) => b.kind.type !== 'sceneBreak');
  switch (matter) {
    case 'contents':
      return headingParagraphs([section.title], 'ChapterHeading', newPage)
        + contentsEntries(m).map((e) => paragraph('BodyFirst', run(e.text, PLAIN, m))).join('');
    case 'copyright':
      return pageBreak + blocks.map((b) => paragraph('BodyFirst', inlines(b.inlines, m), b.alignment)).join('');
    default:
      // Half title, title page, dedication, epigraph: centred.
      return pageBreak + blocks.map((b) => paragraph('SceneBreak', inlines(b.inlines, m))).join('');
  }
}

function headingParagraphs(lines: string[], style: string, newPage: boolean): string {
  return lines.map((line, i) => {
    const pageBreak = i === 0 && newPage ? '<w:pageBreakBefore/>' : '';
    const spacing = i === 0 ? '' : '<w:spacing w:before="0"/>';
    return `<w:p><w:pPr><w:pStyle w:val="${style}"/>${pageBreak}${spacing}</w:pPr>` + run(line, PLAIN, null) + '</w:p>';
  }).join('');
}

function blockXML(block: Block, m: CompiledManuscript, noIndent: boolean, list: number | null): string {
  switch (block.kind.type) {
    case 'sceneBreak':
      return paragraph('SceneBreak', run(m.preset.render.sceneBreak, PLAIN, m));
    case 'paragraph': {
      // Centred and right-aligned lines take no first-line indent.
      const unindented = block.alignment === 'center' || block.alignment === 'right';
      return paragraph(noIndent || unindented ? 'BodyFirst' : 'Body', inlines(block.inlines, m), block.alignment, unindented);
    }
    case 'listItem': {
      const numPr = list !== null ? `<w:numPr><w:ilvl w:val="0"/><w:numId w:val="${list}"/></w:numPr>` : '';
      return `<w:p><w:pPr><w:pStyle w:val="ListParagraph"/>${numPr}</w:pPr>${inlines(block.inlines, m)}</w:p>`;
    }
    case 'blockQuote':
      return paragraph('Quote', inlines(block.inlines, m));
    case 'epigraph':
      return paragraph('Epigraph', inlines(block.inlines, m));
    case 'verse': {
      // A paragraph a line, in the Verse styles (a step is a style of its own, so a poet can restyle every step at once
      // in Word and the import reads them back); the stanza's last line carries the gap after it.
      const lines = verseLines(block);
      const jc = block.alignment !== null ? `<w:jc w:val="${block.alignment === 'justify' ? 'both' : block.alignment}"/>` : '';
      return lines.map((line, n) => {
        const style = line.indent > 0 && block.alignment === null ? `VerseIndent${line.indent}` : 'Verse';
        const after = n === lines.length - 1 ? '<w:spacing w:after="240"/>' : '';
        const ind = block.alignment !== null ? '<w:ind w:left="0" w:hanging="0"/>' : '';
        return `<w:p><w:pPr><w:pStyle w:val="${style}"/>${after}${ind}${jc}</w:pPr>${inlines(line.inlines, m)}</w:p>`;
      }).join('');
    }
    case 'footnote':
      return paragraph('Quote', run(`[${block.kind.id}] `, PLAIN, m) + inlines(block.inlines, m));
  }
}

function inlines(list: Inline[], m: CompiledManuscript): string {
  let inLink = false;
  return list.map((inline) => {
    switch (inline.type) {
      case 'text': return run(inline.text, inline.style, m, inLink);
      case 'lineBreak': return '<w:r><w:br/></w:r>';
      case 'footnoteReference': return `<w:r><w:rPr><w:vertAlign w:val="superscript"/></w:rPr><w:t>${escape(inline.id)}</w:t></w:r>`;
      case 'linkStart':
        inLink = true;
        return `<w:hyperlink r:id="${linkID(inline.address)}">`;
      case 'linkEnd':
        if (!inLink) return '';
        inLink = false;
        return '</w:hyperlink>';
    }
  }).join('') + (inLink ? '</w:hyperlink>' : '');
}

/** `DocxWriter.linkID`: a link's relationship id, from its address, so the text and `document.xml.rels` agree without
 *  passing a table about. */
export const linkID = (address: string) => 'rIdLink' + sha256Hex(address, 8);

/** `DocxWriter.linkAddresses`: every address the manuscript links to, in order of first appearance. */
export function linkAddresses(m: CompiledManuscript): string[] {
  const seen = new Set<string>();
  for (const section of m.sections) {
    for (const block of section.blocks) for (const inline of block.inlines) if (inline.type === 'linkStart') seen.add(inline.address);
  }
  return [...seen];
}

/** `noIndent` clears the style's first-line indent. CT_PPr's order: pStyle … ind … jc. */
function paragraph(style: string, runs: string, alignment: Alignment | null = null, noIndent = false): string {
  const ind = noIndent ? '<w:ind w:firstLine="0"/>' : '';
  const jc = alignment !== null ? `<w:jc w:val="${alignment === 'justify' ? 'both' : alignment}"/>` : '';
  return `<w:p><w:pPr><w:pStyle w:val="${style}"/>${ind}${jc}</w:pPr>${runs}</w:p>`;
}

function run(text: string, style: InlineStyle, m: CompiledManuscript | null, link = false): string {
  // CT_RPr's schema order: rStyle, b, i, smallCaps, color, highlight, u. The writer's highlights and colours are
  // written only when the preset keeps them (`keepsMarks`): an editor's copy.
  const italicAsUnderline = m?.preset.render.italicAsUnderline === true;
  const marks = m !== null && keepsMarks(m.preset);
  let props = link ? '<w:rStyle w:val="Hyperlink"/>' : '';
  if (style.bold) props += '<w:b/>';
  if (style.italic && !italicAsUnderline) props += '<w:i/>';
  if (style.smallCaps) props += '<w:smallCaps/>';
  if (marks && style.color !== null) props += `<w:color w:val="${DOCX_TEXT_COLORS[style.color]}"/>`;
  if (marks && style.highlight !== null) props += `<w:highlight w:val="${DOCX_HIGHLIGHTS[style.highlight]}"/>`;
  if (style.underline || (style.italic && italicAsUnderline)) props += '<w:u w:val="single"/>';
  return '<w:r>' + (props === '' ? '' : `<w:rPr>${props}</w:rPr>`) + `<w:t xml:space="preserve">${escape(text)}</w:t></w:r>`;
}

/** `DocxMarks.highlight`: Word's highlighter has sixteen fixed colours (ST_HighlightColor), so each ink takes the
 *  nearest. Word import (`wordHighlightInk`) brings every one back as an ink; orange comes back as yellow, the nearest it has. */
export const DOCX_HIGHLIGHTS: Record<InkColor, string> = {
  yellow: 'yellow', green: 'green', blue: 'cyan', pink: 'magenta', purple: 'darkMagenta', orange: 'darkYellow', red: 'red', grey: 'lightGray',
};

/** `DocxMarks.color`: the text colour exactly — the Paper page's text ink (`InkPalette.text(_, dark: false)`), as RRGGBB. */
export const DOCX_TEXT_COLORS: Record<InkColor, string> = {
  red: 'B23A2E', orange: 'B85E17', yellow: '8F7209', green: '3E7D3A', blue: '2F5E9E', pink: 'AE4577', purple: '6E4A9E', grey: '7A736B',
};

// MARK: Parts

function styles(r: RenderSettings, language: string): string {
  const size = whole(r.font.size * 2, 2, 3276);
  const line = whole(r.lineSpacing * 240, 1, 31_680);
  const indent = twips(r.firstLineIndent);
  const after = whole(r.paragraphSpacing * 20, 0, 31_680);
  const jc = r.justify ? 'both' : 'left';
  const font = escape(r.font.family);
  const { width: pageWidth, height: pageHeight } = pageInches(r.page.size);
  const drop = twips(Math.max(0, Math.min(r.chapterDrop, pageHeight / 2)));
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">`
    + `<w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="${font}" w:hAnsi="${font}" w:eastAsia="${font}" w:cs="${font}"/><w:sz w:val="${size}"/><w:szCs w:val="${size}"/><w:lang w:val="${escape(language === '' ? 'en-AU' : language)}"/></w:rPr></w:rPrDefault>`
    + `<w:pPrDefault><w:pPr><w:spacing w:after="${after}" w:line="${line}" w:lineRule="auto"/></w:pPr></w:pPrDefault></w:docDefaults>`
    + '<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/></w:style>'
    + `<w:style w:type="paragraph" w:styleId="Body"><w:name w:val="Body Text"/><w:basedOn w:val="Normal"/><w:qFormat/><w:pPr><w:ind w:firstLine="${indent}"/><w:jc w:val="${jc}"/></w:pPr></w:style>`
    + `<w:style w:type="paragraph" w:styleId="BodyFirst"><w:name w:val="Body Text First"/><w:basedOn w:val="Body"/><w:pPr><w:ind w:firstLine="${r.paragraphSpacing > 0 ? 0 : indent}"/></w:pPr></w:style>`
    + '<w:style w:type="paragraph" w:styleId="ListParagraph"><w:name w:val="List Paragraph"/><w:basedOn w:val="Normal"/><w:qFormat/><w:pPr><w:ind w:left="720" w:hanging="360"/></w:pPr></w:style>'
    + '<w:style w:type="paragraph" w:styleId="SceneBreak"><w:name w:val="Scene Break"/><w:basedOn w:val="Normal"/><w:pPr><w:keepNext/><w:jc w:val="center"/></w:pPr></w:style>'
    + `<w:style w:type="paragraph" w:styleId="Quote"><w:name w:val="Quote"/><w:basedOn w:val="Normal"/><w:pPr><w:ind w:left="720" w:right="720"/><w:jc w:val="${jc}"/></w:pPr></w:style>`
    + '<w:style w:type="paragraph" w:styleId="Epigraph"><w:name w:val="Epigraph"/><w:basedOn w:val="Quote"/><w:rPr><w:i/></w:rPr></w:style>'
    + verseStyles()
    + `<w:style w:type="paragraph" w:styleId="ChapterHeading"><w:name w:val="heading 2"/><w:basedOn w:val="Normal"/><w:next w:val="BodyFirst"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="${drop}" w:after="${line}"/><w:jc w:val="center"/><w:outlineLvl w:val="1"/></w:pPr></w:style>`
    + '<w:style w:type="paragraph" w:styleId="PartHeading"><w:name w:val="heading 1"/><w:basedOn w:val="ChapterHeading"/><w:qFormat/><w:pPr><w:outlineLvl w:val="0"/></w:pPr><w:rPr><w:b/></w:rPr></w:style>'
    + `<w:style w:type="paragraph" w:styleId="Contact"><w:name w:val="Contact"/><w:basedOn w:val="Normal"/><w:pPr><w:tabs><w:tab w:val="right" w:pos="${twips(pageWidth - 2 * r.page.margins)}"/></w:tabs><w:spacing w:line="240" w:lineRule="auto"/></w:pPr></w:style>`
    + '<w:style w:type="paragraph" w:styleId="ContactRight"><w:name w:val="Contact Right"/><w:basedOn w:val="Contact"/><w:pPr><w:jc w:val="right"/></w:pPr></w:style>'
    + `<w:style w:type="paragraph" w:styleId="TitlePageTitle"><w:name w:val="Title"/><w:basedOn w:val="Normal"/><w:qFormat/><w:pPr><w:spacing w:before="${twips(pageHeight / 3)}"/><w:jc w:val="center"/></w:pPr></w:style>`
    + '<w:style w:type="paragraph" w:styleId="Byline"><w:name w:val="Byline"/><w:basedOn w:val="Normal"/><w:pPr><w:jc w:val="center"/></w:pPr></w:style>'
    + '<w:style w:type="character" w:styleId="Hyperlink"><w:name w:val="Hyperlink"/><w:uiPriority w:val="99"/><w:unhideWhenUsed/><w:rPr><w:color w:val="0563C1"/><w:u w:val="single"/></w:rPr></w:style>'
    + '<w:style w:type="paragraph" w:styleId="Header"><w:name w:val="header"/><w:basedOn w:val="Normal"/><w:pPr><w:spacing w:line="240" w:lineRule="auto"/><w:jc w:val="right"/></w:pPr></w:style>'
    + '</w:styles>';
}

/** `DocxWriter.verseStyles`: `Verse` (left, a 0.3 in hanging indent, no space after, no hyphenation) and `Verse Indent
 *  1`–`6`, each a step of 0.3 in further in; single-spaced whatever the body's spacing. */
export function verseStyles(): string {
  const step = twips(0.3);
  const base = `<w:style w:type="paragraph" w:styleId="Verse"><w:name w:val="Verse"/><w:basedOn w:val="Normal"/><w:qFormat/><w:pPr><w:suppressAutoHyphens/><w:spacing w:after="0" w:line="240" w:lineRule="auto"/><w:ind w:left="${step}" w:hanging="${step}"/><w:jc w:val="left"/></w:pPr></w:style>`;
  let steps = '';
  for (let n = 1; n <= MAX_VERSE_INDENT; n++) {
    steps += `<w:style w:type="paragraph" w:styleId="VerseIndent${n}"><w:name w:val="Verse Indent ${n}"/><w:basedOn w:val="Verse"/><w:pPr><w:ind w:left="${step * (n + 1)}" w:hanging="${step}"/></w:pPr></w:style>`;
  }
  return base + steps;
}

function headerXML(text: string, mark: string | null): string {
  const parts = text.split('{page}');
  let runs = '';
  parts.forEach((part, i) => {
    if (part !== '') runs += `<w:r><w:t xml:space="preserve">${escape(part)}</w:t></w:r>`;
    if (i < parts.length - 1) {
      runs += '<w:r><w:fldChar w:fldCharType="begin"/></w:r><w:r><w:instrText xml:space="preserve"> PAGE </w:instrText></w:r><w:r><w:fldChar w:fldCharType="separate"/></w:r><w:r><w:t>1</w:t></w:r><w:r><w:fldChar w:fldCharType="end"/></w:r>';
    }
  });
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:hdr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
    + (runs === '' ? '' : `<w:p><w:pPr><w:pStyle w:val="Header"/></w:pPr>${runs}</w:p>`)
    + (mark !== null ? `<w:p><w:pPr><w:pStyle w:val="Header"/><w:jc w:val="center"/></w:pPr><w:r><w:rPr><w:color w:val="777777"/><w:sz w:val="16"/></w:rPr><w:t xml:space="preserve">${escape(mark)}</w:t></w:r></w:p>` : '')
    + (runs === '' && mark === null ? '<w:p/>' : '') + '</w:hdr>';
}

const EMPTY_HEADER = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:hdr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:p/></w:hdr>`;

function custom(properties: Record<string, string>): string {
  const items = Object.entries(properties).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([key, value], i) =>
    `<property fmtid="{D5CDD505-2E9C-101B-9397-08002B2CF9AE}" pid="${i + 2}" name="${escape(key)}"><vt:lpwstr>${escape(value)}</vt:lpwstr></property>`).join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/custom-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">${items}</Properties>`;
}

function contentTypes(header: boolean, custom: boolean, numbering: boolean): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
    + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>'
    + '<Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>'
    + '<Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/>'
    + (numbering ? '<Override PartName="/word/numbering.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.numbering+xml"/>' : '')
    + (header ? '<Override PartName="/word/header1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/><Override PartName="/word/header2.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/>' : '')
    + '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>'
    + '<Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>'
    + (custom ? '<Override PartName="/docProps/custom.xml" ContentType="application/vnd.openxmlformats-officedocument.custom-properties+xml"/>' : '')
    + '</Types>';
}

function rootRels(custom: boolean): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>'
    + '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>'
    + '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>'
    + (custom ? '<Relationship Id="rId4" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/custom-properties" Target="docProps/custom.xml"/>' : '')
    + '</Relationships>';
}

function documentRels(header: boolean, numbering: boolean, links: string[] = []): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + '<Relationship Id="rIdStyles" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>'
    + '<Relationship Id="rIdSettings" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/settings" Target="settings.xml"/>'
    + (numbering ? '<Relationship Id="rIdNumbering" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/numbering" Target="numbering.xml"/>' : '')
    + (header ? '<Relationship Id="rIdHeader1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header1.xml"/><Relationship Id="rIdHeader2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header2.xml"/>' : '')
    + links.map((a) => `<Relationship Id="${linkID(a)}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink" Target="${escape(a)}" TargetMode="External"/>`).join('')
    + '</Relationships>';
}

const SETTINGS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:defaultTabStop w:val="720"/><w:characterSpacingControl w:val="doNotCompress"/><w:compat><w:compatSetting w:name="compatibilityMode" w:uri="http://schemas.microsoft.com/office/word" w:val="15"/></w:compat></w:settings>`;

/** Core properties. Blind presets leave creator and last-modified-by empty. */
function core(m: CompiledManuscript, date: Date): string {
  const author = m.preset.blind ? '' : escape(m.author ? authorNames(m.author).join('; ') : '');
  const stamp = iso8601(date);
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">`
    + `<dc:title>${escape(m.title)}</dc:title><dc:creator>${author}</dc:creator><cp:lastModifiedBy>${author}</cp:lastModifiedBy>`
    + `<dcterms:created xsi:type="dcterms:W3CDTF">${stamp}</dcterms:created><dcterms:modified xsi:type="dcterms:W3CDTF">${stamp}</dcterms:modified>`
    + '</cp:coreProperties>';
}

function app(m: CompiledManuscript): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"><Application>Five Rooms</Application><Words>${m.wordCount}</Words>${m.preset.blind ? '' : '<Company></Company>'}</Properties>`;
}

/** The parts a play's Word document shares with a book's (`StageDocx` uses `DocxWriter.settings/core/app/custom`). */
export { app as docxApp, core as docxCore, custom as docxCustom, SETTINGS as DOCX_SETTINGS };
